import React, { useCallback, useEffect, useState } from "react";

import {
  fetchAdminTaskById,
  fetchAdminTasks,
} from "../../../api/adminData";
import useEditDetailLoader from "../../../hooks/useEditDetailLoader";
import { formatDateOnly, formatDateTime } from "../../../utils/datetime";
import { ENTITY_LINK_TYPES } from "../entityLinks/multiEntityLinks";
import TaskFormModal, { TASK_TYPE_OPTIONS } from "./TaskFormModal";

/** ``filter`` is an API QUERY shape — one singular id like
 * ``{ tenancy_id }``, which is what GET /api/admin/tasks accepts. The form
 * holds only the seven PLURAL manual sets (ME3a removed the singular
 * ``*_id`` fields from it entirely), so handing the filter straight to
 * ``initialFormData`` left a stray ``tenancy_id`` key that
 * ``formToPayload`` never reads — a task created from an entity tab came
 * out UNLINKED, on every mount site.
 *
 * Derived from the link registry rather than a second hand-written list of
 * the seven types, so a new entity type is prefilled here the moment it is
 * registered. Same translation ``createDefaultLinks`` already does for
 * work reports (workReportFormState.js). */
const IDS_FIELD_BY_SINGULAR = Object.fromEntries(
  ENTITY_LINK_TYPES.map((d) => [`${d.type}_id`, d.idsField])
);

/** The tab's filter as form state: one prefilled manual set per type.
 * Keys that are not entity links, and ids that are still falsy because the
 * parent has not loaded yet, are dropped — the form must not open holding
 * ``tenancy_ids: [""]``. */
export function entityFilterToFormLinks(filter) {
  const links = {};
  Object.entries(filter || {}).forEach(([key, value]) => {
    const idsField = IDS_FIELD_BY_SINGULAR[key];
    if (idsField && value) links[idsField] = [String(value)];
  });
  return links;
}

// Status labels mirrored from AdminTasksPage's local STATUS_OPTIONS (not
// exported there). Badge colours are THEME-AWARE (light/dark pairs) to match
// the host detail page — NOT the dark-only modal / AdminTasksPage hex.
const STATUS_LABEL = {
  todo: "Offen",
  in_progress: "In Arbeit",
  blocked: "Blockiert",
  done: "Erledigt",
  cancelled: "Abgebrochen",
};

const STATUS_BADGE = {
  todo:
    "border-slate-200 bg-slate-100 text-slate-600 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-300",
  in_progress:
    "border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  blocked:
    "border-rose-300 bg-rose-100 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  done:
    "border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
  cancelled:
    "border-slate-200 bg-slate-100 text-slate-500 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-400",
};

const STATUS_BADGE_FALLBACK =
  "border-slate-200 bg-slate-100 text-slate-600 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-300";

/**
 * The date one task row shows, exactly as this tab renders it: the due date,
 * or the start when there is no due date, all-day rows without a time.
 *
 * Extracted from the row `.map` below so the tenancy overview's task preview
 * can print the SAME string. A second copy of this three-line rule is how the
 * preview and the tab start disagreeing about the same task.
 */
export function taskRowDateLabel(task) {
  const d = task?.due_at || task?.start_at;
  if (!d) return "—";
  return task.all_day ? formatDateOnly(d) : formatDateTime(d);
}

// Re-exported for surfaces that show tasks WITHOUT re-implementing this tab —
// the tenancy overview preview reads exactly these maps, so a status renders
// with one label and one colour everywhere.
export {
  STATUS_LABEL as TASK_STATUS_LABEL,
  STATUS_BADGE as TASK_STATUS_BADGE,
  STATUS_BADGE_FALLBACK as TASK_STATUS_BADGE_FALLBACK,
};

const TYPE_LABEL = Object.fromEntries(
  TASK_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

/** Everything about a task row that is NOT its arrangement: the surface, the
 *  padding, the hover and the disabled state. Both layouts share it verbatim,
 *  so the two can never drift into looking like different components. */
const ROW_SHELL =
  "rounded-md border border-black/10 bg-white px-3 py-2 text-left transition-colors hover:border-[#FF7A3D] disabled:opacity-50 dark:border-white/[0.07] dark:bg-[#1a2030] dark:hover:border-[#FF7A3D]";

/**
 * Reusable "Aufgaben" section for an entity detail surface. Lists the tasks
 * linked to ONE entity (``filter`` = a single-key object like
 * ``{ application_id: id }``) with create + click-to-edit via TaskFormModal,
 * reloading on save. Theme-aware (matches the host page); the embedded modal
 * keeps its own dark-only styling. Reused unchanged on Mieter / Unit /
 * Vermieter / Mietverhältnis — only the mount site differs.
 *
 * ``rowLayout`` — how ONE task row arranges itself. Two values, one behaviour:
 * the same data, the same button, the same click target, only a different
 * arrangement.
 *
 *   "inline"  (default) title · status · type · date · assignee on one
 *             wrapping line. What every mount site has always rendered.
 *   "stacked" title and status on the first line, type · date · assignee as a
 *             quieter second line.
 *
 * THE DEFAULT IS A SCOPE BOUNDARY, NOT A PREFERENCE. "stacked" reads better on
 * any of the six surfaces that mount this tab, and the honest change would be
 * to make it the only layout. That would restyle Bewerbung, Vermieter, Mieter,
 * Unit and the tenant page's TenancyCard in a slice whose scope is the
 * Mietverhältnis workspace, without anyone having looked at those five. So the
 * fork exists until they are, and this prop is the thing to delete then — not
 * a knob to add a third value to.
 */
export default function EntityTasksTab({
  filter,
  title,
  bare = false,
  rowLayout = "inline",
}) {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedTask, setSelectedTask] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  // ``filter`` is an inline object → a new reference every parent render.
  // Depend on a STABLE key (not the object) to avoid a refetch loop, and read
  // the filter back from the key inside loadTasks so [filterKey] is the only
  // dependency.
  const filterKey = JSON.stringify(filter || {});

  const loadTasks = useCallback(async () => {
    const f = JSON.parse(filterKey);
    const filterValue = Object.values(f)[0];
    // Falsy id (entity not loaded yet) → skip the fetch; a falsy filter would
    // otherwise return the org's FULL unfiltered task list.
    if (!filterValue) {
      setTasks([]);
      setTotal(0);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const d = await fetchAdminTasks({ ...f, limit: 50 });
      setTasks(d.items || []);
      setTotal(d.total ?? (d.items?.length || 0));
      setError(null);
    } catch (e) {
      setError(e?.message || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  function openCreate() {
    setModalMode("create");
    setSelectedTask(null);
    setInitialFormData(entityFilterToFormLinks(filter));
    setModalOpen(true);
  }

  // PICK1a: this tab lists TaskListItem rows, which carry no link
  // ARRAYS — only the legacy singular ids. Opening the form on one and
  // saving would write back at most one link per type and delete the
  // rest, so the row supplies an id and the form gets the full TaskRead.
  const { requestEdit: openEdit, pendingId: editPendingId } =
    useEditDetailLoader({
      fetchDetail: fetchAdminTaskById,
      onLoaded: useCallback((detail) => {
        setModalMode("edit");
        setSelectedTask(detail);
        setInitialFormData(null);
        setModalOpen(true);
      }, []),
      errorMessage: "Die Aufgabe konnte nicht geladen werden.",
    });

  const primaryBtn =
    "rounded-md border border-[#FF7A3D] bg-[#FF7A3D] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90";

  const body = loading ? (
    <p className="m-0 text-sm text-slate-500 dark:text-slate-400">Lädt…</p>
  ) : error ? (
    <div className="flex flex-wrap items-center gap-2 text-sm text-red-700 dark:text-red-300">
      <span>{error}</span>
      <button
        type="button"
        onClick={loadTasks}
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#FF7A3D] hover:text-[#FF7A3D] dark:border-white/10 dark:bg-[#141824] dark:text-slate-200"
      >
        Erneut versuchen
      </button>
    </div>
  ) : tasks.length === 0 ? (
    <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
      Keine Aufgaben
    </p>
  ) : (
    <div className="flex flex-col gap-2">
      {tasks.map((task) => {
        const dateLabel = taskRowDateLabel(task);
        const badge = STATUS_BADGE[task.status] || STATUS_BADGE_FALLBACK;
        const statusPill = (
          <span
            className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge}`}
          >
            {STATUS_LABEL[task.status] || task.status}
          </span>
        );
        const typeLabel = TYPE_LABEL[task.task_type] || task.task_type;
        const assignee = task.assigned_to_email || "Nicht zugewiesen";

        return (
          <button
            key={task.id}
            type="button"
            onClick={() => openEdit(task)}
            disabled={editPendingId === String(task.id)}
            // ONE BUTTON EITHER WAY. The whole row stays the click target and
            // the focus ring; only the arrangement inside it differs, so
            // keyboard reach, `disabled` and the reading order are the same in
            // both. The "inline" string is byte-identical to what this tab has
            // always emitted — five other surfaces render it and none of them
            // is in this slice's scope.
            className={
              rowLayout === "stacked"
                ? `flex w-full flex-col gap-[3px] ${ROW_SHELL}`
                : `flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1 ${ROW_SHELL}`
            }
          >
            {rowLayout === "stacked" ? (
              <>
                {/* Line one is the question "which task is this, and where
                    does it stand" — nothing else competes for it. `break-words`
                    rather than `truncate`: on a phone a long title wraps under
                    itself instead of being cut, and the pill keeps its own
                    column because it never shrinks. */}
                <span className="flex w-full items-start justify-between gap-2">
                  <span className="min-w-0 flex-1 break-words text-sm font-medium text-slate-800 dark:text-white">
                    {task.title}
                  </span>
                  {statusPill}
                </span>
                {/* Line two is context, at one weight and one size. The dots
                    are `aria-hidden`: a screen reader reads three facts, not
                    "Reinigung dot 28.08.2026 dot nicht zugewiesen". */}
                <span className="flex w-full flex-wrap items-center gap-x-2 gap-y-[2px] text-[12px] text-slate-500 dark:text-slate-400">
                  <span>{typeLabel}</span>
                  <span aria-hidden="true">·</span>
                  <span className="tabular-nums">{dateLabel}</span>
                  <span aria-hidden="true">·</span>
                  <span className="min-w-0 break-all">{assignee}</span>
                </span>
              </>
            ) : (
              <>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-white">
                  {task.title}
                </span>
                {statusPill}
                <span className="shrink-0 text-[11px] text-slate-500 dark:text-slate-400">
                  {typeLabel}
                </span>
                <span className="shrink-0 text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
                  {dateLabel}
                </span>
                <span className="shrink-0 text-[11px] text-slate-500 dark:text-slate-400">
                  {assignee}
                </span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );

  const modal = (
    <TaskFormModal
      open={modalOpen}
      mode={modalMode}
      task={selectedTask}
      initialFormData={initialFormData}
      onOpenChange={(o) => {
        setModalOpen(o);
        if (!o) {
          setSelectedTask(null);
          setInitialFormData(null);
        }
      }}
      onSaved={() => {
        setModalOpen(false);
        setSelectedTask(null);
        setInitialFormData(null);
        loadTasks();
      }}
    />
  );

  // Bare (tab) variant: no outer card, no <h3> — the surrounding DetailTabs
  // already provides the surface + the "Aufgaben" label. Just a button row +
  // the list. The carded default is unchanged (used by section mounts).
  if (bare) {
    return (
      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            {total
              ? `${total} ${total === 1 ? "Aufgabe" : "Aufgaben"}`
              : ""}
          </span>
          <button type="button" onClick={openCreate} className={primaryBtn}>
            Neue Aufgabe
          </button>
        </div>
        {body}
        {modal}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-[14px] border border-black/10 bg-white p-6 dark:border-white/[0.07] dark:bg-[#141824]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="m-0 text-[9px] font-bold uppercase tracking-[1px] text-slate-500 dark:text-slate-400">
          {title}
          {total ? ` (${total})` : ""}
        </h3>
        <button type="button" onClick={openCreate} className={primaryBtn}>
          Neue Aufgabe
        </button>
      </div>
      {body}
      {modal}
    </div>
  );
}
